import React from 'react'
import { Stack, Typography } from '@mui/material'
import { FormTable, FormTableRow } from './FormTable'
import {
    RegisteredDecimalField,
    RegisteredIntegerField,
    RegisteredNumericField,
} from '../../../components/fields/NumericField'
import { ShouldShow, ShouldShowAny, showField } from '../../../components/entities/IssueType'
import { RegisteredCheckBox } from '../../../components/fields/CheckBox'
import { useFormContext } from 'react-hook-form'
import { formatMoney } from '../../../packages/utils/numberFormatters'
import { useTranslation } from 'react-i18next'
import { RegisteredTextField } from '../../../components/fields/TextField'
import { variableLabel } from './variableLabel'
import { FormIssueEntity } from '../types/FormIssueEntity'

function TermsFields({
    issue,
    noSecondaryOfferingChecked,
    setIsCustomFormDirty,
}: {
    issue: FormIssueEntity
    noSecondaryOfferingChecked: boolean
    setIsCustomFormDirty: (v: boolean) => void
}) {
    const { i18n } = useTranslation()
    const locale = i18n.language
    const formMethods = useFormContext()
    const abbTarget = formMethods.watch('abbTarget')
    const offeredUnits = parseInt(formMethods.watch('offeredUnits')) || 0
    const unitSubPrice = parseFloat(formMethods.watch('unitSubPrice')) || 0
    const sharesInUnit = parseInt(formMethods.watch('sharesInUnit')) || 1
    const pricePerShare = unitSubPrice / sharesInUnit

    const pricePerShareIntervalLow = (parseFloat(formMethods.watch('ipoIntervalLow')) || 0) / sharesInUnit
    const pricePerShareIntervalHigh = (parseFloat(formMethods.watch('ipoIntervalHigh')) || 0) / sharesInUnit

    const totalOfferedUnits = offeredUnits + (parseInt(formMethods.watch('offeredUnitsUnlisted')) || 0)
    const issueSize = unitSubPrice * totalOfferedUnits
    const noOverAllotmentChecked = !!formMethods.watch('noOverAllotment')
    const rto = formMethods.watch('rto')
    const earnout = formMethods.watch('earnout')

    if (!issue) return null
    return (
        <FormTable>
            <ShouldShowAny
                issueType={issue.type}
                issueFields={['sharesRequired', 'rightsReceived', 'rightsRequired', 'unitsReceived']}
            >
                <FormTableRow label='Villkor'>
                    <Stack spacing={4}>
                        {!issue.termsDate && (
                            <RegisteredCheckBox
                                label='Teckningskurs offentliggörs senare'
                                name='noTermsInitially'
                                onChange={() => setIsCustomFormDirty(true)}
                            />
                        )}
                        <Stack direction='row' spacing={1}>
                            <RegisteredNumericField label='Antal aktier' name='sharesRequired' decimals={0} />
                            <Typography variant='body2' sx={{ fontSize: 30 }}>
                                ={'>'}
                            </Typography>
                            <RegisteredNumericField label='Antal rätter' name='rightsReceived' decimals={0} />
                        </Stack>
                        <Stack direction='row' spacing={1}>
                            <RegisteredNumericField label='Antal rätter' name='rightsRequired' decimals={0} />
                            <Typography variant='body2' sx={{ fontSize: 30 }}>
                                ={'>'}
                            </Typography>
                            <RegisteredNumericField
                                label={variableLabel('unitsReceived', issue)}
                                name='unitsReceived'
                                decimals={0}
                            />
                        </Stack>
                    </Stack>
                </FormTableRow>
            </ShouldShowAny>
            <ShouldShow issueType={issue.type} issueField='sharesInUnit'>
                <FormTableRow label='Aktier per unit'>
                    <Stack direction='row' spacing={2}>
                        <RegisteredNumericField label='Aktier per unit' name='sharesInUnit' decimals={0} />
                        <RegisteredTextField
                            name='unitExtraContent'
                            sx={{ width: '100%' }}
                            label='Extraordinärt innehåll i unit (inte TO)'
                            multiline
                            minRows={1}
                        />
                    </Stack>
                </FormTableRow>
            </ShouldShow>
            <ShouldShowAny issueType={issue.type} issueFields='ipoIntervalLow, ipoIntervalHigh'>
                <FormTableRow label='Pris per unit (intervall)'>
                    <Stack direction='row' spacing={2} alignItems='center'>
                        <RegisteredDecimalField label={variableLabel('ipoIntervalLow', issue)} name='ipoIntervalLow' />
                        <RegisteredDecimalField
                            label={variableLabel('ipoIntervalHigh', issue)}
                            name='ipoIntervalHigh'
                        />
                        <Typography variant='body2'>
                            {`Pris per aktie:
                            ${formatMoney(pricePerShareIntervalLow, {
                                currencySymbol: issue.currency,
                                minDecimals: 2,
                                maxDecimals: 5,
                                locale,
                            })}
                             -
                            ${formatMoney(pricePerShareIntervalHigh, {
                                currencySymbol: issue.currency,
                                minDecimals: 2,
                                maxDecimals: 5,
                                locale,
                            })}`}
                        </Typography>
                    </Stack>
                </FormTableRow>
            </ShouldShowAny>
            <ShouldShow issueType={issue.type} issueField='unitSubPrice'>
                <FormTableRow label={variableLabel('unitSubPrice', issue)}>
                    <Stack direction='row' spacing={2} alignItems='center'>
                        <RegisteredDecimalField label={variableLabel('unitSubPrice', issue)} name='unitSubPrice' />
                        <Typography variant='body2'>
                            Pris per aktie:{' '}
                            {formatMoney(pricePerShare, {
                                currencySymbol: issue.currency,
                                minDecimals: 2,
                                maxDecimals: 5,
                                locale,
                            })}
                        </Typography>
                    </Stack>
                </FormTableRow>
            </ShouldShow>
            <ShouldShow issueType={issue.type} issueField='offeredUnits'>
                <FormTableRow
                    label={variableLabel('offeredUnits', issue)}
                    hasNoBorderBottom={showField(issue.type, 'offeredUnitsUnlisted')}
                >
                    <Stack direction='row' spacing={2} alignItems='center'>
                        <RegisteredIntegerField
                            label={variableLabel('offeredUnits', issue)}
                            name='offeredUnits'
                            disabled={!!abbTarget}
                        />
                        <Typography variant='body2'>
                            Emissionsstorlek:{' '}
                            {formatMoney(issueSize, {
                                currencySymbol: issue.currency,
                                minDecimals: 0,
                                maxDecimals: 5,
                                locale,
                            })}
                        </Typography>
                    </Stack>
                    <ShouldShow issueType={issue.type} issueField='spac'>
                        <RegisteredCheckBox
                            label='SPAC'
                            name='spac'
                            disabled={rto}
                            onChange={() => {
                                setIsCustomFormDirty(true)
                            }}
                        />
                    </ShouldShow>
                </FormTableRow>
            </ShouldShow>
            <ShouldShow issueType={issue.type} issueField='offeredUnitsUnlisted'>
                <FormTableRow label='Antal units i onoterade aktieslag'>
                    <RegisteredIntegerField
                        label='Antal units'
                        name='offeredUnitsUnlisted'
                        disabled={false}
                        sx={{ marginRight: 4 }}
                    />
                </FormTableRow>
            </ShouldShow>
            <ShouldShow issueType={issue.type} issueField='noncashConsideration'>
                <FormTableRow label='Apportegendom'>
                    <RegisteredTextField
                        label='Apportegendom'
                        name='noncashConsideration'
                        fullWidth
                        multiline
                        minRows={3}
                    />

                    <ShouldShow issueType={issue.type} issueField='earnout'>
                        <Stack direction='row' alignItems='center'>
                            <RegisteredCheckBox
                                label='Tilläggsköpeskilling'
                                name='earnout'
                                disabled={rto}
                                onChange={() => {
                                    setIsCustomFormDirty(true)
                                }}
                            />
                        </Stack>
                    </ShouldShow>
                    <ShouldShow issueType={issue.type} issueField='rto'>
                        <Stack direction='row' alignItems='center'>
                            <RegisteredCheckBox
                                label='Omvänt förvärv (RTO)'
                                name='rto'
                                disabled={earnout}
                                onChange={() => {
                                    setIsCustomFormDirty(true)
                                }}
                            />
                        </Stack>
                    </ShouldShow>
                </FormTableRow>
            </ShouldShow>
            <ShouldShow issueType={issue.type} issueField='abbTarget'>
                <FormTableRow label='Cirka belopp'>
                    <RegisteredIntegerField
                        label='Cirka belopp' // TODO should add currency in label
                        name='abbTarget'
                        disabled={!!offeredUnits}
                    />
                </FormTableRow>
            </ShouldShow>

            <ShouldShow issueType={issue.type} issueField='secondaryOffering'>
                <FormTableRow label='Befintliga aktier som erbjuds (secondary)'>
                    <Stack direction='row' spacing={2}>
                        {!noSecondaryOfferingChecked && (
                            <RegisteredIntegerField
                                label='Antal aktier'
                                helperText='Utöver de som erbjuds i emissionen'
                                name='secondaryOffering'
                            />
                        )}
                        <RegisteredCheckBox
                            label='Inga befintliga aktier erbjuds'
                            name='noSecondaryOffering'
                            onChange={() => {
                                setIsCustomFormDirty(true)
                            }}
                        />
                    </Stack>
                </FormTableRow>
            </ShouldShow>

            <ShouldShow issueType={issue.type} issueField='noOverAllotment'>
                <FormTableRow label='Övertilldelning antal units'>
                    <Stack direction='row' spacing={2}>
                        {!noOverAllotmentChecked && (
                            <Stack spacing={4}>
                                {totalOfferedUnits !== 0 ? (
                                    <RegisteredIntegerField
                                        label='Övertilldelning antal units'
                                        name='overAllotmentSize'
                                    />
                                ) : null}
                                {!noSecondaryOfferingChecked ? (
                                    <ShouldShow issueType={issue.type} issueField='overAllotmentSizeSecondary'>
                                        <RegisteredIntegerField
                                            label='Övertilldelning (secondary)'
                                            name='overAllotmentSizeSecondary'
                                        />
                                    </ShouldShow>
                                ) : null}
                            </Stack>
                        )}
                        <RegisteredCheckBox
                            label='Ingen övertilldelning'
                            name='noOverAllotment'
                            onChange={() => {
                                setIsCustomFormDirty(true)
                            }}
                        />
                    </Stack>
                </FormTableRow>
            </ShouldShow>

            <ShouldShow issueType={issue.type} issueField='minimumSubCondition'>
                <FormTableRow label='Villkorad teckningsgrad'>
                    <RegisteredNumericField
                        label='Teckningsgrad'
                        name='minimumSubCondition'
                        minDecimals={0}
                        maxDecimals={2}
                        percentage={true}
                    />
                </FormTableRow>
            </ShouldShow>
        </FormTable>
    )
}

export default TermsFields
